<template>
  <div class="gif-picker">
    <div class="gif-picker__search">
      <v-text-field
        v-model="searchQuery"
        :placeholder="$t('gif.search')"
        dense
        outlined
        hide-details
        clearable
        background-color="gray15"
        class="gif-picker__input base-filter"
      />
    </div>
    <div
      v-if="!searchQuery"
      class="gif-picker__label caption gray70--text px-3 pt-2 pb-1"
    >
      {{ $t("gif.trending") }}
    </div>
    <div class="gif-picker__grid">
      <div
        v-if="loading"
        class="gif-picker__loading d-flex justify-center align-center"
      >
        <v-progress-circular indeterminate size="24" color="purple100" />
      </div>
      <div
        v-else-if="gifs.length === 0"
        class="gif-picker__empty caption gray70--text text-center pa-4"
      >
        {{ $t("gif.notfound") }}
      </div>
      <div v-else class="gif-picker__items">
        <img
          v-for="gif in gifs"
          :key="gif.id"
          :src="gif.previewUrl"
          class="gif-picker__item"
          loading="lazy"
          alt="GIF"
          @click="selectGif(gif)"
        />
      </div>
    </div>
    <div class="gif-picker__attribution caption gray70--text text-center py-1">
      Powered by Tenor
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import _ from "lodash";

interface TenorGif {
  id: string;
  url: string;
  previewUrl: string;
}

const TENOR_API_KEY = process.env.VUE_APP_TENOR_API_KEY || "LIVDSRZULELA";
const TENOR_BASE_URL = "https://api.tenor.com/v1";
const TENOR_UNAVAILABLE_KEY = "tenor:unavailable_until";
const TENOR_UNAVAILABLE_TTL = 60 * 60 * 1000;

@Component
export default class GifPicker extends Vue {
  searchQuery = "";
  gifs: TenorGif[] = [];
  loading = false;
  debouncedSearch: (() => void) | null = null;

  created(): void {
    this.debouncedSearch = _.debounce(this.fetchGifs, 600);
    if (this.isTenorUnavailable()) {
      this.$emit("api-unavailable");
    }
  }

  mounted(): void {
    if (!this.isTenorUnavailable()) {
      this.fetchTrending();
    }
  }

  beforeDestroy(): void {
    if (this.debouncedSearch) {
      (this.debouncedSearch as any).cancel();
    }
  }

  @Watch("searchQuery")
  onSearchChange(val: string): void {
    if (val) {
      if (this.debouncedSearch) this.debouncedSearch();
    } else {
      if (this.debouncedSearch) (this.debouncedSearch as any).cancel();
      this.fetchTrending();
    }
  }

  async fetchTrending(): Promise<void> {
    this.loading = true;
    try {
      const params = new URLSearchParams({
        key: TENOR_API_KEY,
        limit: "20",
        media_filter: "basic",
        contentfilter: "high",
        locale: "pt_BR",
      });
      const response = await fetch(`${TENOR_BASE_URL}/trending?${params}`);
      if (!response.ok) {
        if ([429, 401, 403].includes(response.status)) {
          this.setTenorUnavailable();
        }
        return;
      }
      const data = await response.json();
      this.gifs = this.mapResults(data.results || []);
    } catch {
      this.gifs = [];
    } finally {
      this.loading = false;
    }
  }

  async fetchGifs(): Promise<void> {
    if (!this.searchQuery.trim()) return;
    this.loading = true;
    try {
      const params = new URLSearchParams({
        q: this.searchQuery.trim(),
        key: TENOR_API_KEY,
        limit: "20",
        media_filter: "basic",
        contentfilter: "high",
        locale: "pt_BR",
      });
      const response = await fetch(`${TENOR_BASE_URL}/search?${params}`);
      if (!response.ok) {
        if ([429, 401, 403].includes(response.status)) {
          this.setTenorUnavailable();
        }
        return;
      }
      const data = await response.json();
      this.gifs = this.mapResults(data.results || []);
    } catch {
      this.gifs = [];
    } finally {
      this.loading = false;
    }
  }

  isTenorUnavailable(): boolean {
    const val = localStorage.getItem(TENOR_UNAVAILABLE_KEY);
    if (!val) return false;
    return Date.now() < Number(val);
  }

  setTenorUnavailable(): void {
    localStorage.setItem(
      TENOR_UNAVAILABLE_KEY,
      String(Date.now() + TENOR_UNAVAILABLE_TTL)
    );
    this.$emit("api-unavailable");
  }

  mapResults(results: any[]): TenorGif[] {
    return results
      .filter(
        (r: any) => r.media?.[0]?.nanogif?.url || r.media?.[0]?.tinygif?.url
      )
      .map((r: any) => {
        const media = r.media[0];
        const url = (media.nanogif || media.tinygif).url;
        return { id: r.id, url, previewUrl: url };
      });
  }

  selectGif(gif: TenorGif): void {
    this.$emit("select", gif);
  }
}
</script>

<style lang="sass">
.gif-picker
  background: var(--v-white-base)
  border: 1px solid var(--v-gray35-base)
  border-radius: 8px
  width: 300px
  max-height: 400px
  display: flex
  flex-direction: column
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
  overflow: hidden

  &__search
    padding: 8px 8px 4px

  &__label
    line-height: 1.2

  &__grid
    overflow-y: auto
    flex: 1
    min-height: 100px

  &__loading
    height: 120px

  &__empty
    height: 120px
    display: flex
    align-items: center
    justify-content: center

  &__items
    display: grid
    grid-template-columns: repeat(2, 1fr)
    gap: 4px
    padding: 4px 8px 8px

  &__item
    width: 100%
    height: 90px
    object-fit: cover
    border-radius: 4px
    cursor: pointer
    transition: opacity 0.2s
    display: block

    &:hover
      opacity: 0.8

  &__attribution
    border-top: 1px solid var(--v-gray35-base)
    font-size: 10px !important
</style>
